import * as React from "react";
import {
    Alert,
    Box,
    CircularProgress,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Toolbar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CheckCircle, Error as ErrorIcon, FiberManualRecord } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { DISPLAY_DATE_FORMAT } from "./DataTable";

export const PROCESS_DRAWER_WIDTH = 270;

const PROCESS_NAME_INDEX_CALCULATION = "index_calculation";
const PROCESS_NAME_CPI_CALCULATION = "cpi_calculation";
const PROCESS_NAME_DSS_IMPORT = "securities_import_DSSImporter";
const PROCESS_NAME_FX_IMPORT = "fx_import";
const PROCESS_NAME_TASE_PAYMENTS = "tase_payments";
const PROCESS_NAME_TASE_EOD = "securities_import_TASEEODImporter";
const PROCESS_NAME_TASE_NEXT_OPEN = "securities_import_TASENextOpenImporter";
const PROCESS_NAME_DSS_GLOBAL_BONDS = "securities_import_DSSGlobalBondsImporter";
// Live reporting related processes
const PROCESS_NAME_LIVE_PRICES = "live_prices_heartbeat";
const PROCESS_NAME_LIVE_INDEX_LEVELS = "index_levels_heartbeat";
const PROCESS_NAME_LAST_LIVE_SNAPSHOT = "rt_levels_snapshots";

const LIVE_PROCESS_NAMES = [
    PROCESS_NAME_LIVE_PRICES,
    PROCESS_NAME_LIVE_INDEX_LEVELS,
    PROCESS_NAME_LAST_LIVE_SNAPSHOT,
];

const INDEX_CALCULATION_TYPES = {
    main: "Main Calculation",
    tase_early: "TASE Early Calculation",
    late: "Late",
    recalculation: "Recalculation",
    indices_ca: "Indices CA Report",
};

// Processes shown in the drawer, by group
const PROCESS_GROUPS = [
    {
        title: "Processes",
        processes: [
            { name: PROCESS_NAME_INDEX_CALCULATION, label: "Index Calculation Process" },
            { name: PROCESS_NAME_FX_IMPORT, label: "FX Import" },
            { name: PROCESS_NAME_DSS_IMPORT, label: "DSS Securities Import" },
            { name: PROCESS_NAME_DSS_GLOBAL_BONDS, label: "DSS Global Bonds Import" },
            { name: PROCESS_NAME_CPI_CALCULATION, label: "CPI Calculation" },
            { name: PROCESS_NAME_TASE_PAYMENTS, label: "TASE Payments" },
            { name: PROCESS_NAME_TASE_EOD, label: "TASE EOD" },
            { name: PROCESS_NAME_TASE_NEXT_OPEN, label: "TASE Next Open" },
        ],
    },
    {
        title: "Live Reporting",
        processes: [
            { name: PROCESS_NAME_LIVE_PRICES, label: "Live Prices Fetching" },
            { name: PROCESS_NAME_LIVE_INDEX_LEVELS, label: "Live Index Calculation" },
            { name: PROCESS_NAME_LAST_LIVE_SNAPSHOT, label: "Last Live Snapshot" },
        ],
    },
];

const DISPLAYED_PROCESS_NAMES = PROCESS_GROUPS.flatMap((g) =>
    g.processes.map((p) => p.name),
);

// The status each live process reports when it's healthy
const LIVE_PROCESS_HEALTHY_STATUSES = {
    [PROCESS_NAME_LIVE_PRICES]: "running",
    [PROCESS_NAME_LIVE_INDEX_LEVELS]: "running",
    [PROCESS_NAME_LAST_LIVE_SNAPSHOT]: "finished",
};
// Live processes that report a heartbeat, and are considered stale if it's too old
const HEARTBEAT_PROCESS_NAMES = [
    PROCESS_NAME_LIVE_PRICES,
    PROCESS_NAME_LIVE_INDEX_LEVELS,
];
// A heartbeat older than this is considered stale
const LIVE_PROCESS_STALE_MINUTES = 5;

/**
 * Returns a short description of what's wrong with a process, or null if it's fine
 * @param name process name
 * @param process process status, as returned from the backend (undefined if missing)
 * @param currentProcesses all process statuses returned from the backend
 */
function getProcessProblem(name, process, currentProcesses) {
    if (!process) {
        // An empty list means statuses haven't been loaded yet
        return currentProcesses.length > 0 ? "Missing from response" : null;
    }

    if (process.status === "failed") {
        return "Failed";
    }

    if (LIVE_PROCESS_NAMES.includes(name)) {
        const healthyStatus = LIVE_PROCESS_HEALTHY_STATUSES[name];
        if (String(process.status).toLowerCase() !== healthyStatus) {
            return `Status is not "${healthyStatus}"`;
        }
    }

    if (HEARTBEAT_PROCESS_NAMES.includes(name)) {
        if (typeof process.timestamp !== "number") {
            return "No timestamp";
        }

        const minutesAgo = Math.floor(
            DateTime.now()
                .diff(DateTime.fromMillis(process.timestamp))
                .as("minutes"),
        );
        if (minutesAgo > LIVE_PROCESS_STALE_MINUTES) {
            return `Stale - last update ${minutesAgo} minutes ago`;
        }
    }

    return null;
}

/**
 * Returns the number of displayed processes with a problem (failed, unhealthy, stale or missing)
 */
export function countProcessProblems(currentProcesses) {
    return DISPLAYED_PROCESS_NAMES.filter((name) =>
        getProcessProblem(
            name,
            currentProcesses.find((x) => x.name === name),
            currentProcesses,
        ),
    ).length;
}

// Process timestamps may be missing from the backend response, and fromMillis throws on non-numbers
const formatTimestamp = (millis) =>
    typeof millis === "number"
        ? DateTime.fromMillis(millis).toFormat(DISPLAY_DATE_FORMAT)
        : "N/A";

const formatTimestampWithTime = (millis) =>
    typeof millis === "number"
        ? DateTime.fromMillis(millis).toFormat(`${DISPLAY_DATE_FORMAT} HH:mm`)
        : "N/A";

function formatProcess(name, process, indexesById) {
    if (!process) {
        return "Loading...";
    }

    // Different formatting for the live reporting processes
    if (name === PROCESS_NAME_LIVE_PRICES || name === PROCESS_NAME_LIVE_INDEX_LEVELS) {
        return `${process.status} (${formatTimestamp(process.timestamp)})`;
    }

    if (name === PROCESS_NAME_LAST_LIVE_SNAPSHOT) {
        return `${process.status} (${formatTimestampWithTime(process.timestamp)})`;
    }

    // Default formatting
    if (process.status === "finished") {
        return `Finished (${formatTimestamp(
            process.start_date
                ? process.start_date
                : process.import_process_calc_date,
        )})`;
    } else if (process.status === "failed") {
        return `Failed (${formatTimestamp(
            process.first_import_timestamp,
        )}) - ${process.reason ? process.reason.substring(0, 150) : ""
            }`;
    }

    if (name === PROCESS_NAME_INDEX_CALCULATION) {
        let status = `Running ${INDEX_CALCULATION_TYPES[process.calculation_type]
            } (${formatTimestamp(process.start_date)})`;

        if (
            process.calculation_type === "recalculation" &&
            process.index_id
        ) {
            const index = indexesById[process.index_id];
            status += ` for Index ${index ? index.internal_id : "N/A"}`;
        }

        return status;
    }
}

function ProcessStatusIcon({ name, process, problem }) {
    if (problem) {
        return <ErrorIcon color="error" />;
    }
    if (!process) {
        // Still loading
        return <FiberManualRecord color="disabled" />;
    }
    if (process.status === "finished" || LIVE_PROCESS_NAMES.includes(name)) {
        return <CheckCircle color="success" />;
    }
    // Neither finished nor failed - still running
    return <CircularProgress size={20} />;
}

/**
 * Right-side drawer showing the statuses of the backend processes
 * @param open whether the drawer is open
 */
export default function ProcessStatusDrawer({ open }) {
    const currentProcesses = useSelector(
        (state) => state.indexes.currentProcesses,
    );
    const indexesById = useSelector((state) => state.indexes.indexesById);
    const problemCount = countProcessProblems(currentProcesses);

    return (
        <Drawer
            variant="persistent"
            anchor="right"
            open={open}
            sx={{
                width: open ? PROCESS_DRAWER_WIDTH : 0,
                flexShrink: 0,
                transition: (theme) =>
                    theme.transitions.create("width", {
                        easing: theme.transitions.easing.sharp,
                        duration: open
                            ? theme.transitions.duration.enteringScreen
                            : theme.transitions.duration.leavingScreen,
                    }),
                "& .MuiDrawer-paper": {
                    width: PROCESS_DRAWER_WIDTH,
                    boxSizing: "border-box",
                },
            }}
        >
            <Toolbar />
            {currentProcesses.length > 0 && (
                <Alert
                    severity={problemCount > 0 ? "error" : "success"}
                    sx={{ m: 2, mb: 0 }}
                >
                    {problemCount > 0
                        ? `${problemCount} process${problemCount > 1 ? "es" : ""
                        } need${problemCount > 1 ? "" : "s"} attention`
                        : "All processes OK"}
                </Alert>
            )}
            {PROCESS_GROUPS.map((group) => (
                <List
                    key={group.title}
                    dense
                    subheader={<ListSubheader>{group.title}</ListSubheader>}
                >
                    {group.processes.map(({ name, label }) => {
                        const process = currentProcesses.find(
                            (x) => x.name === name,
                        );
                        const problem = getProcessProblem(
                            name,
                            process,
                            currentProcesses,
                        );
                        const status = formatProcess(
                            name,
                            process,
                            indexesById,
                        );
                        return (
                            <ListItem
                                key={name}
                                alignItems="flex-start"
                                sx={
                                    problem && {
                                        bgcolor: (theme) =>
                                            alpha(
                                                theme.palette.error.main,
                                                0.08,
                                            ),
                                    }
                                }
                            >
                                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                                    <ProcessStatusIcon
                                        name={name}
                                        process={process}
                                        problem={problem}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={label}
                                    primaryTypographyProps={{
                                        fontWeight: "bold",
                                    }}
                                    secondary={
                                        <>
                                            {/* A missing process shows only its problem, instead of "Loading..." */}
                                            {(process || !problem) && status}
                                            {/* A failed process's status already says it failed */}
                                            {problem && process?.status !== "failed" && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: "block",
                                                        color: "error.main",
                                                    }}
                                                >
                                                    {problem}
                                                </Box>
                                            )}
                                        </>
                                    }
                                />
                            </ListItem>
                        );
                    })}
                </List>
            ))}
        </Drawer>
    );
}
